/**
 * Activate Account Page
 * 
 * Handles email activation link clicks.
 * Reads the activation token from URL query params and activates the account.
 */

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Mail, ArrowRight } from 'lucide-react';
import { apiClient, ApiResponse, ApiClientError } from '../services/api';

type ActivationStatus = 'loading' | 'success' | 'error' | 'already_activated' | 'invalid_token';

interface ActivationResult {
  success: boolean;
  message: string;
}

const Activate: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<ActivationStatus>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const activateAccount = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setStatus('invalid_token');
        setMessage('No activation token provided');
        return;
      }

      try {
        const response = await apiClient.post<ApiResponse<ActivationResult>>('/auth/activate', { token });
        const result = response.data.data;

        if (result.success) {
          if (result.message.includes('already activated')) {
            setStatus('already_activated');
            setMessage('Your account is already activated. You can login now.');
          } else {
            setStatus('success');
            setMessage('Your account has been activated successfully!');
          }
        } else {
          setStatus('error');
          setMessage(result.message || 'Failed to activate account');
        }
      } catch (error) {
        const apiErr = error as ApiClientError;
        const errorMessage = apiErr.response?.data?.message || apiErr.message || 'Failed to activate account';

        if (errorMessage.includes('Invalid') || errorMessage.includes('expired')) {
          setStatus('invalid_token');
          setMessage('This activation link is invalid or has expired. Please contact your administrator.');
        } else {
          setStatus('error');
          setMessage(errorMessage);
        }
      }
    };

    activateAccount();
  }, [searchParams]);

  const handleGoToLogin = () => {
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-8 text-center">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">
              Account Activation
            </h1>
            <p className="text-white/80 text-sm mt-1">
              HAAT Hub
            </p>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Loading State */}
            {status === 'loading' && (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 mb-2">
                  Activating your account...
                </h2>
                <p className="text-gray-500 text-sm">
                  Please wait a moment
                </p>
              </div>
            )}

            {/* Success State */}
            {status === 'success' && (
              <div className="text-center py-8">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', duration: 0.5 }}
                  className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"
                >
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </motion.div>
                <h2 className="text-lg font-semibold text-gray-900 mb-2">
                  Account Activated!
                </h2>
                <p className="text-gray-500 text-sm mb-6">
                  {message}
                </p>
                <button
                  onClick={handleGoToLogin}
                  className="
                    inline-flex items-center justify-center gap-2
                    px-6 py-3 rounded-xl
                    bg-gradient-to-r from-primary-500 to-primary-600
                    text-white font-medium
                    hover:from-primary-600 hover:to-primary-700
                    transition-all duration-200
                    shadow-lg shadow-primary-500/25
                  "
                >
                  Go to Login
                  <ArrowRight size={18} />
                </button>
              </div>
            )}

            {/* Already Activated State */}
            {status === 'already_activated' && (
              <div className="text-center py-8">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', duration: 0.5 }}
                  className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4"
                >
                  <CheckCircle className="w-8 h-8 text-blue-500" />
                </motion.div>
                <h2 className="text-lg font-semibold text-gray-900 mb-2">
                  Already Activated
                </h2>
                <p className="text-gray-500 text-sm mb-6">
                  {message}
                </p>
                <button
                  onClick={handleGoToLogin}
                  className="
                    inline-flex items-center justify-center gap-2
                    px-6 py-3 rounded-xl
                    bg-gradient-to-r from-primary-500 to-primary-600
                    text-white font-medium
                    hover:from-primary-600 hover:to-primary-700
                    transition-all duration-200
                    shadow-lg shadow-primary-500/25
                  "
                >
                  Go to Login
                  <ArrowRight size={18} />
                </button>
              </div>
            )}

            {/* Error State */}
            {(status === 'error' || status === 'invalid_token') && (
              <div className="text-center py-8">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', duration: 0.5 }}
                  className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"
                >
                  <XCircle className="w-8 h-8 text-[#c00035]" />
                </motion.div>
                <h2 className="text-lg font-semibold text-gray-900 mb-2">
                  {status === 'invalid_token' ? 'Invalid Link' : 'Activation Failed'}
                </h2>
                <p className="text-gray-500 text-sm mb-6">
                  {message}
                </p>
                <button
                  onClick={handleGoToLogin}
                  className="
                    inline-flex items-center justify-center gap-2
                    px-6 py-3 rounded-xl
                    bg-gray-100 text-gray-700 font-medium
                    hover:bg-gray-200
                    transition-all duration-200
                  "
                >
                  Back to Login
                  <ArrowRight size={18} />
                </button>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 text-center">
            <p className="text-xs text-gray-500">
              Having trouble? Contact your administrator for assistance.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Activate;
